/**
 * Route guard for the billing-entry surface (PATIENT-PAYMENTS-REVENUE-
 * SPINE-V1). Mirrors RequireWorkboard: the pure decision lives in
 * decideBillingEntryAccess (billing-entry-view.ts) and keys off the
 * caller's role -- receptionist / coc / owner (the seeded entry role +
 * supervisors). Any other role redirects to its own homePath.
 *
 * This is a role-only UX gate: NO /me flag signal, NO feature flag
 * (internal surface, ADR-0016). Backend route auth -- the CONFIG-BACKED
 * 'billing_entry' routing gate + org/clinic scope -- remains the real
 * security boundary; an org that retargets the entry role to a role
 * outside this set still works through the backend (recorded gap: the
 * nav approximation hides the link for exotic target roles).
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideBillingEntryAccess } from '@/dashboard/billing/billing-entry-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireBillingEntryViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireBillingEntryView({
  loading,
  error,
  me,
  children,
}: RequireBillingEntryViewProps): JSX.Element | null {
  const decision = decideBillingEntryAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireBillingEntry(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireBillingEntryView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
